import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { loadFeaturedStorefrontProducts } from "../data/storefront";
import { addProductToCart, getSuggestedCartAmount } from "../data/cart";

const BestSellers = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      // Load products that the cashier featured
      const loadedProducts = await loadFeaturedStorefrontProducts(10);
      if (isMounted) {
        setProducts(loadedProducts);
      }
    };

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleAddClick = async (product) => {
    try {
      await addProductToCart(product, getSuggestedCartAmount(product));
      await Swal.fire({
        icon: "success",
        title: "Ajouté au panier",
        text: "Produit réservé et ajouté au panier.",
        showConfirmButton: true,
        timer: 2200,
      });
      navigate("/ShopCart");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Stock indisponible",
        text: error.message,
      });
    }
  };

  return (
    <section className="my-lg-14 my-8">
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f8f9fa;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #2b7a5f;
          border-radius: 10px;
          border: 2px solid #f8f9fa;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #1e5a45;
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #2b7a5f #f8f9fa;
        }
      `}</style>
      <div className="container">
        <div className="row g-4 align-items-stretch">
          {/* Left fixed banner/modal style */}
          <div className="col-lg-3 col-md-4">
            <div 
              className="h-100 rounded-4 p-4 p-xl-5 d-flex flex-column justify-content-center text-center text-md-start shadow-sm"
              style={{ backgroundColor: "#2b7a5f", color: "white" }}
            >
              <h2 className="fw-bold mb-4 text-white" style={{ fontSize: "2rem", lineHeight: "1.2" }}>
                Nos meilleures ventes
              </h2>
              <p className="mb-4" style={{ fontSize: "1.05rem", opacity: 0.9 }}>
                Les indispensables choisis par nos clients. Faites le plein d'économies !
              </p>
              <div>
                <Link 
                  to="/Shop" 
                  className="btn text-white rounded-pill px-4 py-2"
                  style={{ border: "2px solid white", fontWeight: "600" }}
                  onMouseOver={(e) => { e.target.style.backgroundColor = 'white'; e.target.style.color = '#2b7a5f'; }}
                  onMouseOut={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = 'white'; }}
                >
                  Voir tout <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="feather feather-arrow-right ms-1"><line x1={5} y1={12} x2={19} y2={12} /><polyline points="12 5 19 12 12 19" /></svg>
                </Link>
              </div>
            </div>
          </div>
          
          {/* Right scrollable products */}
          <div className="col-lg-9 col-md-8">
            <div 
              className="d-flex gap-3 pb-3 h-100 align-items-stretch custom-scrollbar" 
              style={{ overflowX: "auto", scrollSnapType: "x mandatory" }}
            >
              {products.map((product) => (
                <div 
                  key={product.id} 
                  className="card card-product flex-shrink-0" 
                  style={{ width: "260px", scrollSnapAlign: "start" }}
                >
                  <div className="card-body d-flex flex-column h-100">
                    <div className="text-center position-relative mb-3">
                      {product.badge && (
                        <div className="position-absolute top-0 start-0">
                          <span className="badge bg-danger">{product.badge}</span>
                        </div>
                      )}
                      <Link to="/SingleShop">
                        <img src={product.image} alt={product.name} className="img-fluid" style={{ maxHeight: "150px", objectFit: "contain" }} />
                      </Link>
                    </div>
                    
                    <div className="text-small mb-1">
                      <Link to="/Shop" className="text-decoration-none text-muted"><small>{product.category}</small></Link>
                    </div>
                    
                    <h2 className="fs-6 mb-2 flex-grow-1">
                      <Link to="/SingleShop" className="text-inherit text-decoration-none">{product.name}</Link>
                    </h2>
                    
                    {product.maxPerOrder ? (
                      <div className="small text-muted mb-2">
                        <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle">
                          Max {product.maxPerOrder} par commande
                        </span>
                      </div>
                    ) : null}
                    
                    <div className="mb-3">
                      <small className="text-warning">
                        <i className="bi bi-star-fill" />
                        <i className="bi bi-star-fill" />
                        <i className="bi bi-star-fill" />
                        <i className="bi bi-star-fill" />
                        <i className="bi bi-star-half" />
                      </small>{" "}
                      <span className="text-muted small">{product.rating} ({product.reviews})</span>
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center mt-auto">
                      <div>
                        <span className="text-dark fw-bold">{product.priceLabel}</span>
                        {product.unitLabel ? <div className="small text-muted">{product.unitLabel}</div> : null}
                      </div>
                      <div>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => handleAddClick(product)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="feather feather-plus">
                            <line x1={12} y1={5} x2={12} y2={19} />
                            <line x1={5} y1={12} x2={19} y2={12} />
                          </svg>{" "}
                          Ajouter
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BestSellers;
